import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Table, Button, Select, Space, message } from "antd";
import { ReloadOutlined, RollbackOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { apiClient } from "../../../shared/api/AllApi";
import { formatCurrency } from "../../../utils/formatters";
import { parseLocalizedName } from "../../../utils/localization";
import { readCachedSystemConfig } from "../../../shared/lib/systemConfig";
import { formatRangeParams, resolveDatePreset } from "../../../shared/lib/dateFilterPresets";
import { orderDocNo } from "../../../shared/lib/documentNumbers";
import { PageHeader, StatCard, T, cardStyle } from "../../../shared/ui/PageShell";
import DateRangeFilter from "../../../shared/ui/DateRangeFilter";
import useTablePagination from "../../../shared/ui/useTablePagination";

const ReturnsList = () => {
  const { t, i18n } = useTranslation();
  const { pagination, resetPage } = useTablePagination();
  const currency = readCachedSystemConfig()?.currency_code || "IQD";

  const [range, setRange] = useState(() => resolveDatePreset("today", { withTime: true }));
  const [vendorId, setVendorId] = useState(null);
  const [customerId, setCustomerId] = useState(null);
  const [reason, setReason] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        ...formatRangeParams(range, { withTime: true, fromKey: "date_from", toKey: "date_to" }),
      };
      if (vendorId) params.vendor_id = vendorId;
      if (customerId) params.customer_id = customerId;
      if (reason) params.reason = reason;
      const r = await apiClient.get("/pos/returns", { params });
      setData(r.data || null);
    } catch (e) {
      setData(null);
      message.error(e?.response?.data?.message || t("loadError", "Failed to load"));
    } finally {
      setLoading(false);
    }
  }, [range, vendorId, customerId, reason, t]);

  useEffect(() => { load(); resetPage(); }, [load]);
  useEffect(() => {
    apiClient.get("/vendors")
      .then((r) => setVendors(Array.isArray(r.data) ? r.data : []))
      .catch(() => setVendors([]));
    apiClient.get("/customers")
      .then((r) => setCustomers(Array.isArray(r.data) ? r.data : (r.data?.data || [])))
      .catch(() => setCustomers([]));
  }, []);

  const reasonOptions = useMemo(() => {
    const set = new Set((data?.by_reason || []).map((r) => r.reason).filter(Boolean));
    return Array.from(set).map((v) => ({ value: v, label: v }));
  }, [data]);

  const vendorOptions = useMemo(
    () => vendors.map((v) => ({ value: v.id, label: v.name || `#${v.id}` })),
    [vendors]
  );

  const customerOptions = useMemo(
    () => customers.map((c) => ({
      value: c.id,
      label: c.phone_number ? `${c.name || `#${c.id}`} (${c.phone_number})` : (c.name || `#${c.id}`),
    })),
    [customers]
  );

  const returns = data?.returns || [];

  const columns = [
    {
      title: <b>{t("date", "Date")}</b>,
      dataIndex: "created_at",
      key: "created_at",
      width: 150,
      render: (v) => dayjs(v).format("DD/MM/YYYY HH:mm"),
    },
    {
      title: <b>{t("posReturnsSaleId", "Sale #")}</b>,
      dataIndex: "order_id",
      key: "order_id",
      width: 110,
      render: (id, r) => (
        <Link to={`/orders/${id}/view`} style={{ fontWeight: 700, fontFamily: "monospace", fontSize: 12 }}>
          {orderDocNo(r)}
        </Link>
      ),
    },
    {
      title: <b>{t("customer", "Customer")}</b>,
      dataIndex: "customer",
      key: "customer",
      ellipsis: true,
      render: (v, r) => (
        r.customer_id ? (
          <Link to={`/customers/${r.customer_id}`}>{v || "—"}</Link>
        ) : (v || t("walkIn", "Walk-in"))
      ),
    },
    {
      title: <b>{t("product", "Product")}</b>,
      key: "product",
      render: (_, r) => parseLocalizedName(r.product, i18n.language, `#${r.product_id}`),
    },
    {
      title: <b>{t("posPurchaseVendor", "Farmer")}</b>,
      dataIndex: "vendor",
      key: "vendor",
      render: (v) => v || "—",
    },
    {
      title: <b>{t("quantity", "Qty")}</b>,
      key: "return_qty",
      align: "right",
      width: 90,
      render: (_, r) => {
        const q = Number(r.return_qty) || 0;
        return q > 0 ? q : <span style={{ color: T.muted }}>—</span>;
      },
    },
    {
      title: <b>{t("weight", "Weight")}</b>,
      key: "return_weight",
      align: "right",
      width: 100,
      render: (_, r) => {
        const w = Number(r.return_weight) || 0;
        return w > 0 ? `${w} ${t("kg", "KG")}` : <span style={{ color: T.muted }}>—</span>;
      },
    },
    {
      title: <b>{t("price", "Price")}</b>,
      dataIndex: "unit_price",
      key: "unit_price",
      align: "right",
      width: 110,
      render: (v) => formatCurrency(v, currency),
    },
    {
      title: <b>{t("posReturnsRefund", "Refund")}</b>,
      dataIndex: "refund_amount",
      key: "refund_amount",
      align: "right",
      width: 120,
      render: (v, r) => (
        <span>
          <b style={{ color: T.green }}>{formatCurrency(v, currency)}</b>
          {Number(r.commission_refund_amount) > 0 && (
            <div style={{ fontSize: 11, color: T.muted, fontWeight: 400 }}>
              {t("inclCommission", "incl. commission")} {formatCurrency(r.commission_refund_amount, currency)}
            </div>
          )}
        </span>
      ),
    },
    {
      title: <b>{t("cashRefund", "Cash refund")}</b>,
      dataIndex: "cash_refund_amount",
      key: "cash_refund_amount",
      align: "right",
      render: (v) => <span style={{ color: T.red }}>{formatCurrency(v, currency)}</span>,
    },
    {
      title: <b>{t("debtReduced", "Debt reduced")}</b>,
      dataIndex: "credit_reduction_amount",
      key: "credit_reduction_amount",
      align: "right",
      render: (v) => formatCurrency(v, currency),
    },
    {
      title: <b>{t("refundMethod", "Refund method")}</b>,
      dataIndex: "refund_method",
      key: "refund_method",
      render: (v) => v || "—",
    },
    {
      title: <b>{t("reason", "Reason")}</b>,
      dataIndex: "reason",
      key: "reason",
      render: (v) => v || "—",
    },
    {
      title: <b>{t("returnedBy", "Returned by")}</b>,
      dataIndex: "returned_by",
      key: "returned_by",
      render: (v, r) => v || r.user || "—",
    },
    {
      title: <b>{t("soldBy", "Sold by")}</b>,
      dataIndex: "sold_by",
      key: "sold_by",
      render: (v) => v || "—",
    },
  ];

  return (
    <div className="velan-page" style={{ padding: "clamp(12px, 2.5vw, 24px)", background: T.bg, minHeight: "100%" }}>
      <PageHeader
        breadcrumb={[t("orders", "Orders"), t("posReturnsTitle", "Returns")]}
        title={t("posReturnsTitle", "Returns History")}
        subtitle={t("returnsListHint", "All POS product returns (qty + weight). Customer refund includes commission when the sale was taxed. To process a return you need Process Returns permission: open Orders → view a POS sale → Return Items.")}
        action={
          <Space>
            <Button icon={<ReloadOutlined />} style={{ borderRadius: 9 }} onClick={load} loading={loading}>
              {t("refresh", "Refresh")}
            </Button>
            <Link to="/orders">
              <Button type="primary" icon={<RollbackOutlined />} style={{ borderRadius: 9 }}>
                {t("orders", "Orders")}
              </Button>
            </Link>
          </Space>
        }
      />

      <div className="velan-stats-row">
        <StatCard
          icon={<RollbackOutlined style={{ color: "#ef4444" }} />}
          label={t("posReturnsTotal", "Total Returns")}
          value={data?.total_returns ?? 0}
          iconBg="#fee2e2"
        />
        <StatCard
          icon={<RollbackOutlined style={{ color: "#f59e0b" }} />}
          label={t("posReturnsItems", "Items Returned")}
          value={data?.items_returned ?? 0}
          iconBg="#fef3c7"
        />
        <StatCard
          icon={<RollbackOutlined style={{ color: "#16a34a" }} />}
          label={t("posOrdersCashBack", "Total Cash Back")}
          value={formatCurrency(data?.total_refund || 0, currency)}
          iconBg="#dcfce7"
        />
      </div>

      <div style={{ ...cardStyle, marginBottom: 16, padding: "14px 16px" }}>
        <Space wrap size="middle">
          <DateRangeFilter
            value={range}
            onChange={(v) => setRange(v && v[0] && v[1] ? v : resolveDatePreset("today", { withTime: true }))}
            showTime
            allowClear={false}
            size="small"
          />
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder={t("customer", "Customer")}
            value={customerId}
            onChange={setCustomerId}
            options={customerOptions}
            style={{ minWidth: 220 }}
          />
          <Select
            allowClear
            placeholder={t("allFarmers", "All Farmers")}
            value={vendorId}
            onChange={setVendorId}
            options={vendorOptions}
            style={{ minWidth: 180 }}
            showSearch
            optionFilterProp="label"
          />
          <Select
            allowClear
            placeholder={t("allReasons", "All Reasons")}
            value={reason}
            onChange={setReason}
            options={reasonOptions}
            style={{ minWidth: 160 }}
          />
        </Space>
      </div>

      <div style={cardStyle} className="velan-list-shell">
        <div className="velan-list-body">
          <Table
            size="small"
            loading={loading}
            rowKey="id"
            dataSource={returns}
            columns={columns}
            pagination={{ ...pagination, size: "small" }}
            scroll={{ x: "max-content" }}
            locale={{ emptyText: t("noDataFound", "No data found") }}
          />
        </div>
      </div>
    </div>
  );
};

export default ReturnsList;
